import Swal from "sweetalert2";

function htmlToPlainLines(raw) {
  const plain = String(raw || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&");
  return plain
    .split(/\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

/** Plain-text description for list UI (strips HTML and reschedule audit chains). */
export function formatInvoiceFollowupDescription(raw) {
  if (!raw) return "—";
  const lines = htmlToPlainLines(raw);
  if (!lines.length) return "—";
  const original = lines[lines.length - 1].replace(
    /\s*\[\d{4}-\d{2}-\d{2}\]\.?\s*$/i,
    "",
  );
  return original.trim() || lines[lines.length - 1];
}

const FOLLOWUP_NOTE_HEADER_RE =
  /^(.+?)\s*—\s*follow-up\s+\w+\s*\(assigned:\s*(.+?)\)\s*$/i;
const FOLLOWUP_NOTE_DATE_RE = /^Follow up date:\s*(\d{4}-\d{2}-\d{2})\s*$/i;

/**
 * Invoice note audit rows from follow-up side effects (HTML from backend).
 */
export function formatInvoiceFollowupActivityForHistory(raw) {
  const lines = htmlToPlainLines(raw);
  if (!lines.length) {
    return { description: "—", followupDate: null, assigneeName: null };
  }

  let assigneeName = null;
  let rest = [...lines];

  const headerMatch = rest[0]?.match(FOLLOWUP_NOTE_HEADER_RE);
  if (headerMatch) {
    assigneeName = headerMatch[2]?.trim() || null;
    rest = rest.slice(1);
  }

  let followupDate = null;
  const last = rest[rest.length - 1];
  const dateMatch = last?.match(FOLLOWUP_NOTE_DATE_RE);
  if (dateMatch) {
    followupDate = dateMatch[1];
    rest = rest.slice(0, -1);
  }

  const description = rest.join("\n").trim() || "—";

  return { description, followupDate, assigneeName };
}
export function summarizeInvoiceFollowupsForList(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const pending = [];
  const completed = [];
  list.forEach((row) => {
    const name = String(row?.assignee_name || "Unassigned").trim() || "Unassigned";
    const item = { id: row?.id, name };
    if (String(row?.status || "").toLowerCase() === "pending") {
      pending.push(item);
    } else {
      completed.push(item);
    }
  });
  return {
    total: list.length,
    pending,
    completed,
  };
}

const TIP_LIST_LIMIT = 6;

function tipItems(entries, prefix) {
  const slice = entries.slice(0, TIP_LIST_LIMIT);
  const items = slice.map((entry, index) => ({
    key: `${prefix}-${entry.id ?? index}`,
    label: entry.name,
  }));
  const remaining = entries.length - slice.length;
  if (remaining > 0) {
    items.push({
      key: `${prefix}-more`,
      label: `+${remaining} more`,
    });
  }
  return items;
}

/** Structured content for the invoice list bell hover card. */
export function buildInvoiceFollowupHoverTip(summary) {
  if (!summary?.total) {
    return {
      title: "Follow-ups",
      sections: [],
      footer: "No follow-ups yet. Click to add one.",
    };
  }

  const sections = [];
  if (summary.pending?.length) {
    sections.push({
      key: "pending",
      label: `Pending (${summary.pending.length})`,
      items: tipItems(summary.pending, "pending"),
    });
  }
  if (summary.completed?.length) {
    sections.push({
      key: "completed",
      label: `Completed (${summary.completed.length})`,
      items: tipItems(summary.completed, "completed"),
    });
  }

  return {
    title: `${summary.total} follow-up${summary.total === 1 ? "" : "s"}`,
    sections,
    footer: "Click bell to add follow-up",
  };
}

export function mapInvoiceFollowupsToHistoryEntries(followups = []) {
  return (Array.isArray(followups) ? followups : []).map((row) => {
    const status = String(row?.status || "pending").toLowerCase();
    return {
      id: `invoice-followup-${row.id}`,
      followupId: row.id,
      isFollowupActivity: true,
      isFollowupRecord: true,
      followupStatus: status,
      date: row.created_at || row.followup_date || row.updated_at,
      user: row.creator_name || row.assignee_name || "—",
      action: "followup_assigned",
      detail: formatInvoiceFollowupDescription(row.description),
      followupAssignee: row.assignee_name || null,
      followupDate: row.followup_date || null,
      invoiceNumber: row.invoice_number || null,
      result: row.result ? String(row.result).trim() : "",
    };
  });
}

export function showInvoiceFollowupSuccessSwal(response, fallbackAssigneeName = "") {
  const assigneeName =
    response?.data?.assignee_name ||
    String(fallbackAssigneeName || "").trim() ||
    "the assignee";
  const message =
    response?.message ||
    `Follow-up assigned to ${assigneeName}.`;

  Swal.fire({
    title: "Success!",
    text: message,
    icon: "success",
    timer: 1500,
    showConfirmButton: false,
  });
}